// Pure pivot helpers turning flat MetricSeriesRow lists into Recharts-ready
// data. Dependency-free (no "@/..." imports) so the root vitest suite can
// import this module directly — same precedent as app/src/lib/format.ts.
import type { EngineeringChartSpec } from "./chart-specs";

// Structural mirror of MetricSeriesRow from ./api (kept local so this module
// stays alias-free; MetricSeriesRow is assignable to it).
export interface PivotRowInput {
	period: string;
	squad: string;
	engineer: string | null;
	segmentKey: string | null;
	segmentValue: string | null;
	value: number;
	aggregation: "sum" | "avg";
}

export interface PivotedSeries {
	data: Array<Record<string, string | number>>;
	seriesKeys: string[];
}

interface PivotCell {
	sum: number;
	count: number;
	aggregation: "sum" | "avg";
}

const roundMetric = (value: number): number => Number(value.toFixed(2));

const seriesValueFor = (
	row: PivotRowInput,
	series: EngineeringChartSpec["series"]
): string => {
	switch (series) {
		case "squad":
			return row.squad || "unknown";
		case "engineer":
			return row.engineer ?? "unknown";
		case "segmentValue":
			return row.segmentValue ?? "unknown";
		default:
			return "value";
	}
};

const accumulate = (cell: PivotCell | undefined, row: PivotRowInput): PivotCell => {
	if (!cell) {
		return { sum: row.value, count: 1, aggregation: row.aggregation };
	}
	cell.sum += row.value;
	cell.count += 1;
	return cell;
};

const finalize = (cell: PivotCell): number =>
	roundMetric(cell.aggregation === "avg" ? cell.sum / cell.count : cell.sum);

const pivotTotals = (rows: PivotRowInput[], spec: EngineeringChartSpec): PivotedSeries => {
	const cells = new Map<string, PivotCell>();
	for (const row of rows) {
		const seriesKey = seriesValueFor(row, spec.series);
		cells.set(seriesKey, accumulate(cells.get(seriesKey), row));
	}
	const data = [...cells.entries()]
		.map(([label, cell]) => ({ label, value: finalize(cell) }))
		.sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
	return { data, seriesKeys: ["value"] };
};

export const pivotSeries = (
	rows: PivotRowInput[],
	spec: EngineeringChartSpec
): PivotedSeries => {
	if (spec.totals) {
		return pivotTotals(rows, spec);
	}
	const cellsByPeriod = new Map<string, Map<string, PivotCell>>();
	const seriesSet = new Set<string>();
	for (const row of rows) {
		const seriesKey = seriesValueFor(row, spec.series);
		seriesSet.add(seriesKey);
		let periodCells = cellsByPeriod.get(row.period);
		if (!periodCells) {
			periodCells = new Map<string, PivotCell>();
			cellsByPeriod.set(row.period, periodCells);
		}
		periodCells.set(seriesKey, accumulate(periodCells.get(seriesKey), row));
	}
	const seriesKeys = [...seriesSet].sort((a, b) => a.localeCompare(b));
	const periods = [...cellsByPeriod.keys()].sort((a, b) => a.localeCompare(b));
	const data = periods.map((period) => {
		const entry: Record<string, string | number> = { period };
		const periodCells = cellsByPeriod.get(period);
		for (const seriesKey of seriesKeys) {
			const cell = periodCells?.get(seriesKey);
			if (cell) {
				entry[seriesKey] = finalize(cell);
			}
		}
		return entry;
	});
	return { data, seriesKeys };
};

export const limitSeriesToTopN = (
	pivot: PivotedSeries,
	limit: number,
	otherKey = "Other"
): PivotedSeries => {
	if (limit <= 0 || pivot.seriesKeys.length <= limit) {
		return pivot;
	}
	const totals = pivot.seriesKeys
		.filter((key) => key !== otherKey)
		.map((key) => ({
			key,
			total: pivot.data.reduce((sum, entry) => {
				const value = entry[key];
				return sum + (typeof value === "number" ? value : 0);
			}, 0)
		}))
		.sort((a, b) => b.total - a.total || a.key.localeCompare(b.key));
	const topKeys = totals.slice(0, limit).map((entry) => entry.key);
	const topKeySet = new Set(topKeys);
	const hasOther = totals.length > topKeys.length || pivot.seriesKeys.includes(otherKey);
	const data = pivot.data.map((entry) => {
		const next: Record<string, string | number> = {};
		for (const [key, value] of Object.entries(entry)) {
			if (!pivot.seriesKeys.includes(key) || topKeySet.has(key)) {
				next[key] = value;
			}
		}
		if (hasOther) {
			const otherTotal = pivot.seriesKeys.reduce((sum, key) => {
				if (topKeySet.has(key) || key === otherKey) {
					return sum;
				}
				const value = entry[key];
				return sum + (typeof value === "number" ? value : 0);
			}, typeof entry[otherKey] === "number" ? (entry[otherKey] as number) : 0);
			if (otherTotal > 0) {
				next[otherKey] = roundMetric(otherTotal);
			}
		}
		return next;
	});
	return {
		data,
		seriesKeys: hasOther ? [...topKeys, otherKey] : topKeys
	};
};

// --- Squad display modes -------------------------------------------------
// The toolbar's squad dropdown drives which rows charts render and how
// squad-series charts fold. Only member/b2b are displayable squads; rows for
// other squads (e.g. "lt", "unknown") are excluded from the dashboard.

export type SquadMode = "all" | "compare" | "member" | "b2b";

export const DISPLAY_SQUADS = ["member", "b2b"] as const;
export type DisplaySquad = (typeof DISPLAY_SQUADS)[number];

export const ALL_SQUADS_SERIES_KEY = "all squads";

const isDisplaySquad = (squad: string): squad is DisplaySquad =>
	(DISPLAY_SQUADS as readonly string[]).includes(squad);

export const filterDisplayableRows = (
	rows: PivotRowInput[],
	options: { includeNonDisplaySquads?: boolean } = {}
): PivotRowInput[] =>
	options.includeNonDisplaySquads ? rows : rows.filter((row) => isDisplaySquad(row.squad));

/** Fold member+b2b into one combined series ("all" mode). Sum metrics add up;
 * avg metrics become the unweighted mean of the squad means — same contract
 * as the existing month folding of weekly means. */
export const collapseSquadSeries = (rows: PivotRowInput[]): PivotRowInput[] =>
	rows.map((row) => ({ ...row, squad: ALL_SQUADS_SERIES_KEY }));

export const maxSeriesValue = (
	data: PivotedSeries["data"],
	seriesKeys: string[],
	stacked: boolean
): number => {
	let max = 0;
	for (const entry of data) {
		let stackSum = 0;
		for (const key of seriesKeys) {
			const value = entry[key];
			if (typeof value !== "number") {
				continue;
			}
			if (stacked) {
				stackSum += value;
			} else if (value > max) {
				max = value;
			}
		}
		if (stacked && stackSum > max) {
			max = stackSum;
		}
	}
	return max;
};

/** Smallest {1, 2, 2.5, 5, 10} × 10^k at or above value, for round shared
 * axis domains. */
export const niceCeiling = (value: number): number => {
	if (!Number.isFinite(value) || value <= 0) {
		return 0;
	}
	const exponent = Math.floor(Math.log10(value));
	const magnitude = 10 ** exponent;
	for (const step of [1, 2, 2.5, 5, 10]) {
		const candidate = step * magnitude;
		if (value <= candidate) {
			return candidate;
		}
	}
	return 10 * magnitude;
};

export interface CompareSquadPivot {
	/** Always exactly [member, b2b], in that order. */
	panels: Array<{ squad: DisplaySquad; data: PivotedSeries["data"] }>;
	/** Sorted union of series keys across both panels. */
	seriesKeys: string[];
	/** Nice-ceiled max across panels, for a shared Y domain. */
	maxValue: number;
}

/** "Member vs B2B" mode for charts whose series dimension isn't squad: one
 * pivot per squad, rendered as side-by-side panels on a shared scale. */
export const pivotSquadComparison = (
	rows: PivotRowInput[],
	spec: EngineeringChartSpec
): CompareSquadPivot => {
	const stacked = spec.chartType === "stackedBar";
	const seriesSet = new Set<string>();
	let max = 0;
	const panels = DISPLAY_SQUADS.map((squad) => {
		const pivot = pivotSeries(rows.filter((row) => row.squad === squad), spec);
		for (const key of pivot.seriesKeys) {
			seriesSet.add(key);
		}
		const panelMax = maxSeriesValue(pivot.data, pivot.seriesKeys, stacked);
		if (panelMax > max) {
			max = panelMax;
		}
		return { squad, data: pivot.data };
	});
	return {
		panels,
		seriesKeys: [...seriesSet].sort((a, b) => a.localeCompare(b)),
		maxValue: niceCeiling(max)
	};
};

const MONTH_NAMES = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December"
] as const;

/** "2026-07-01" (or "2026-07") → European "01/07/2026" (dd/MM/yyyy). */
export const formatDateEuropean = (isoDate: string): string => {
	const [year, month, day] = isoDate.split("-");
	if (!year || !month) {
		return isoDate;
	}
	return `${day ?? "01"}/${month}/${year}`;
};

/**
 * Collapse per-service segment series (Operational Health cards) into one
 * row per period holding the simple mean across whichever services report
 * that period. Deliberately unweighted — and a mean of per-service p95s is
 * an approximation, fine for a trend line.
 */
export const combineSegmentMeanRows = (rows: PivotRowInput[]): PivotRowInput[] => {
	const byPeriod = new Map<string, { template: PivotRowInput; sum: number; count: number }>();
	for (const row of rows) {
		const entry = byPeriod.get(row.period);
		if (entry) {
			entry.sum += row.value;
			entry.count += 1;
		} else {
			byPeriod.set(row.period, { template: row, sum: row.value, count: 1 });
		}
	}
	return [...byPeriod.values()].map(({ template, sum, count }) => ({
		...template,
		segmentKey: null,
		segmentValue: null,
		value: roundMetric(sum / count),
		aggregation: "avg"
	}));
};

/** Scale raw row values into the chart's display unit (e.g. ms → seconds). */
export const scaleRowValues = (
	rows: PivotRowInput[],
	scale: number
): PivotRowInput[] =>
	rows.map((row) => ({ ...row, value: roundMetric(row.value * scale) }));

/** Tooltip value with the spec's unit: "%"/"$" attach tight, words spaced. */
export const formatValueWithUnit = (value: number, unit?: string): string => {
	const formatted = formatCompactValue(value);
	if (!formatted || !unit) {
		return formatted;
	}
	if (unit === "$") {
		return `$${formatted}`;
	}
	return unit === "%" ? `${formatted}%` : `${formatted} ${unit}`;
};

const COMPACT_NUMBER = new Intl.NumberFormat("en", {
	notation: "compact",
	maximumFractionDigits: 1
});

/** Value-label formatting: compact large numbers ("18B", "1.2M") so labels
 * stay narrow enough to fit inside the chart surface. */
export const formatCompactValue = (value: number): string =>
	Number.isFinite(value) ? COMPACT_NUMBER.format(value) : "";

/** Period label for axes/tooltips: months as "July 2026", weeks as dd/MM/yyyy. */
export const formatPeriodLabel = (
	period: string,
	granularity: "week" | "month"
): string => {
	if (granularity === "month") {
		const [year, month] = period.split("-");
		const name = MONTH_NAMES[Number(month) - 1];
		return year && name ? `${name} ${year}` : period;
	}
	return formatDateEuropean(period);
};

// Stable series colors: the app's chart primary (#1ebbf0, see the WorkspacePage
// chart) first, then complementary hexes drawn from the styles.css palette.
export const CHART_SERIES_PALETTE = [
	"#1ebbf0",
	"#7c3aed",
	"#f45c0f",
	"#3286f4",
	"#0f8f61",
	"#b7791f",
	"#b42318",
	"#7785d8",
	"#0f83c4",
	"#31216b"
] as const;

export const seriesColor = (index: number): string => {
	const size = CHART_SERIES_PALETTE.length;
	return CHART_SERIES_PALETTE[((index % size) + size) % size];
};
